import {
  AlertDetailsList,
  MultiRecordsAlertDetailsList,
} from './AlertDetailsList';
import Label from 'components/form/Label/Label';
import { TitleContainer } from 'components/Containers/TitleContainer/TitleContainer';
import {
  accountDetailsCategory,
  cmAlertTypes,
  inquiryAutoLoanIndustryType,
  inquiryMortgageIndustryType,
} from 'types/cm.alert.constants';
import * as premiumAlertTypes from 'pages/Alerts/types/csid.constants';
import PageLoadingMask from 'components/masks/PageLoadingMask';
import { Link } from 'components/navigation/Link/Link';
import { Row } from 'components/Containers/Row/Row';
import AlertsErrorMask from 'pages/Alerts/components/AlertsErrorMask';
import {
  usePageView,
  useAlertDetailPageView,
  useScrollToTop,
} from 'hook/helper.hook';
import { getNumberOfDaysUntilNextFirstOfMonth } from 'helpers/dateHelper';
import { showCreditProfileRefreshTag } from 'helpers/alerts.helpers';
import { isMobileByOS } from 'externals/_tracking/dom/navigator';
import { SomethingWrong } from 'pages/Alerts/components/SomethingWrong/SomethingWrong';
import AlertActionCard from '../../AlertActionCard/AlertActionCard';
import ScoreDisplay from 'components/Score/ScoreDisplay';
import { CreditScoreRange } from 'components/Score/DashboardScore';
import { ReactComponent as ImprovedAccountLogo } from 'style_icons/improved-account.svg';
import { ReactComponent as DecreaseInCollections } from 'style_icons/decrease-in-collections.svg';
import { ReactComponent as PaidTradeline } from 'style_icons/paid-tradeline.svg';
import { ReactComponent as CreditScoreIncrease } from 'style_icons/credit-score-increase.svg';
import { ReactComponent as CreditScoreDecrease } from 'style_icons/credit-score-decrease.svg';
import { ReactComponent as DarkWebAlert } from 'style_assets/icons/premiumAlerts/dark-web-alert.svg';
import { ReactComponent as NameChangeWebAlert } from 'style_assets/icons/premiumAlerts/name_change_alert.svg';
import { ReactComponent as AddressChangeWebAlert } from 'style_assets/icons/premiumAlerts/address_change_alert.svg';
import { ReactComponent as CourtRecordWebAlert } from 'style_assets/icons/premiumAlerts/court_record_alert.svg';
import { ReactComponent as CreditDial } from 'style_icons/art-dial.svg';
import { ReactComponent as Calender } from 'style_icons/art-calendar.svg';
import { ReactComponent as CreditInquiry } from 'style_icons/art-credit.svg';
import { ReactComponent as Clipboard } from 'style_icons/art-clipboard.svg';
import { ReactComponent as CreditLockIcon } from 'style_assets/icons/icon-tureport.svg';
import { ReactComponent as CreditUsage } from 'style_icons/art-creditusage.svg';
import { ReactComponent as NewAccount } from 'style_icons/art-bank.svg';
import { ReactComponent as AutoInsurance } from 'style_icons/art-carinsurance.svg';
import { ReactComponent as NewAddress } from 'style_icons/art-newaddress.svg';
import { ReactComponent as TuLogo } from 'style_assets/icons/tu_logo.svg';
import { ReactComponent as EquifaxLogo } from 'style_assets/icons/equifax-logo.svg';
import { ReactComponent as ExperianLogo } from 'style_assets/icons/experian-logo.svg';

import classnames from 'classnames';
import '../AlertDetails.scss';
import { decommafy } from 'helpers/number';
import { formatIncome } from 'helpers/money.helper';
import AlertInquiryInfo from './AlertInquiryInfo/AlertInquiryInfo';
import { isObject } from 'helpers/objectHelpers';
import { Image } from 'components/Image/Image';
import { useConfig } from 'hook/config.hook';
import OText from 'components/CText/OText';

const FraudAlertFooter = ({ detailsLinkClick, detailsPageName }) => {
  const onLinkClick = e => {
    detailsLinkClick({
      clickType: 'Transunion Fraud link',
      detailsPageName: detailsPageName,
    });
  };
  return (
    <Label>
      If you have any questions about this fraud alert, visit{' '}
      <a
        onClick={onLinkClick}
        target="_blank"
        href="https://www.transunion.com/personal-credit/credit-disputes/fraud-alerts.page"
        rel="noopener noreferrer"
        className="secondary_text_light bold"
      >
        TransUnion's Fraud Alerts page
      </a>
      .
    </Label>
  );
};

const ScoreFactorsFooter = ({
  showActionsLink,
  detailsLinkClick,
  detailsPageName,
}) => {
  return (
    <Label>
      View the most recent changes that are impacting your score in
      <span>
        {' '}
        <Link
          to={`/factors`}
          onClickCallback={detailsLinkClick}
          clickType={'Score Factors'}
          detailsPageName={detailsPageName}
          className="secondary_text_light"
        >
          Score Factors
        </Link>
      </span>
      {showActionsLink && (
        <span>
          , and check out top actions to take in{' '}
          <Link
            to={`/goals`}
            onClickCallback={detailsLinkClick}
            clickType={'Actions'}
            detailsPageName={detailsPageName}
            className="secondary_text_light"
          >
            My Actions
          </Link>
        </span>
      )}
      .
    </Label>
  );
};

const AlertDetailFooter = ({
  factorPath,
  footerType,
  extraFactorPath,
  extraFooterType,
  detailsLinkClick,
  detailsPageName,
}) => {
  return (
    <Label>
      View the most recent updates to this account and related tips in
      <span>
        {' '}
        <Link
          to={`/factors/${factorPath}`}
          onClickCallback={detailsLinkClick}
          clickType={footerType}
          detailsPageName={detailsPageName}
          className="secondary_text_light"
        >
          {footerType}
        </Link>
      </span>
      {extraFooterType && (
        <span>
          {' '}
          or{' '}
          <Link
            to={`/factors/${extraFactorPath}`}
            onClickCallback={detailsLinkClick}
            clickType={extraFooterType}
            detailsPageName={detailsPageName}
            className="secondary_text_light"
          >
            {extraFooterType}
          </Link>
        </span>
      )}
      .
    </Label>
  );
};

const AlertActionCardList = ({
  type,
  detailsLinkClick,
  detailsPageName,
  datatestid,
  config,
  category,
}) => {
  let icon, description, action, ctaLink;
  const showDescription = config?.showPhxAlertsDescription;
  const isAccountDetailsCategory = accountDetailsCategory.includes(category);
  const isPaidCollection = category === cmAlertTypes?.PAID_COLLECTION;

  switch (type) {
    case 'ACCOUNT_DETAILS':
      icon = <Calender />;
      description =
        showDescription && isAccountDetailsCategory
          ? 'Review your payment history details'
          : showDescription
          ? 'Review your payment history'
          : 'Review your payment history and see what steps you can take to improve it';
      action = 'See Payment History';
      ctaLink = '/dashboard?factor=PAYMENT_HISTORY';
      break;
    case 'ACCOUNT_DETAILS_IMPROVED':
      icon = <Calender />;
      description = showDescription
        ? 'Review your payment history'
        : 'Review your payment history and get tips for maintaining healthy credit';
      action = 'See Payment History';
      ctaLink = '/dashboard?factor=PAYMENT_HISTORY';
      break;
    case 'CREDIT_INQUIRIES':
      icon = <CreditInquiry />;
      description = config?.showPhxAlertsDescription
        ? 'Review your recent hard inquiries'
        : 'See recent inquiries and get tips for keeping your credit healthy';
      action = 'See Credit Inquiries';
      ctaLink = '/dashboard?factor=CREDIT_INQUIRIES';
      break;
    case 'SCORE_FACTORS':
      icon = <CreditDial />;
      description = config?.showPhxAlertsDescription
        ? 'Review all your score factors'
        : 'See what’s impacting your score';
      action = 'See Score Factors';
      ctaLink = '/factors';
      break;
    case 'MY_ACTIONS':
      icon = <Clipboard />;
      description = 'See top actions you can take';
      action = 'See My Actions';
      ctaLink = '/creditManager/goals';
      break;
    case 'REPORT_LOCKED':
    case 'REPORT_FROZEN':
    case 'REPORT_UNLOCKED':
    case 'REPORT_UNFROZEN':
      icon = <CreditLockIcon />;
      description =
        'As a Sesame+ member you can check credit report lock status, lock or unlock your TU Credit Report at any time';
      action = 'Check Credit Lock Status';
      ctaLink = '/creditReports/tabs';
      break;
    case 'CREDIT_USAGE':
      icon = <CreditUsage />;
      description = 'See your credit usage and find related tips';
      action = 'See Credit Usage';
      ctaLink = '/dashboard?factor=CREDIT_USAGE';
      break;
    case 'ACCOUNT_MIX':
    case 'NEW_AUTHORIZED_USER':
      icon = <NewAccount />;
      description = config?.showPhxAlertsDescription
        ? 'Review all your credit accounts'
        : 'View your credit accounts and find helpful tips';
      action = 'See Account Mix';
      ctaLink = '/dashboard?factor=ACCOUNT_MIX';
      break;
    case 'AUTO_INSURANCE':
      icon = <AutoInsurance />;
      description = config?.showPhxAlertsDescription
        ? 'Compare and search for auto insurance rates'
        : 'Compare rates and find the best deal for your new ride';
      action = 'Search Auto Insurance';
      ctaLink = '/offers?vertical=AUTO_INSURANCE';
      break;
    case 'NEW_ADDRESS':
      icon = <NewAddress />;
      description = 'Update your address in your profile';
      action = 'Update Address';
      ctaLink = '/profile';
      break;
    case 'ATTEMPTED_INQUIRY_LOCK':
      description =
        '<div className="notBold text-left">If this was an authorized attempt, unlock your TransUnion credit report and let the requester know to try again. You can lock your report again immediately afterwards. If you did not initiate this attempt, an unauthorized party may have tried to apply for credit in your name. Take a look at <a href="https://www.transunion.com/fraud-victim-resources/fraud-victim-checklist?atvy=%7B%22261809%22%3A%22Experience+B%22%7D" target="_blank">TransUnion’s Fraud Victim Checklist</a> for additional steps you may take.</div>';
      break;
    case 'ATTEMPTED_INQUIRY_FREEZE':
      description =
        '<div className="notBold text-left">If this was an authorized attempt, unfreeze your TransUnion credit report and let the requester know to try again. You can freeze your report again immediately afterwards. If you did not initiate this attempt, an unauthorized party may have tried to apply for credit in your name. Take a look at <a href="https://www.transunion.com/fraud-victim-resources/fraud-victim-checklist?atvy=%7B%22261809%22%3A%22Experience+B%22%7D" target="_blank">TransUnion’s Fraud Victim Checklist</a> for additional steps you may take.</div>';
      break;
    default:
      break;
  }

  return (
    <AlertActionCard
      description={description}
      ctaText={action}
      icon={icon}
      ctaLink={ctaLink}
      detailsLinkClick={detailsLinkClick}
      detailsPageName={detailsPageName}
      datatestid={datatestid}
    />
  );
};

const AlertDetailsFooter = ({
  footerType,
  detailsLinkClick,
  detailsPageName,
}) => {
  switch (footerType) {
    case 'Payment History':
      return (
        <AlertDetailFooter
          factorPath={'paymentHistory'}
          footerType={footerType}
          detailsLinkClick={detailsLinkClick}
          detailsPageName={detailsPageName}
        />
      );
    case 'Credit Usage':
      return (
        <AlertDetailFooter
          factorPath={'creditUsage'}
          footerType={footerType}
          detailsLinkClick={detailsLinkClick}
          detailsPageName={detailsPageName}
        />
      );
    case 'Credit Age':
      return (
        <AlertDetailFooter
          factorPath={'creditAge'}
          footerType={footerType}
          extraFactorPath={'accountMix'}
          extraFooterType={'Account Mix'}
          detailsLinkClick={detailsLinkClick}
          detailsPageName={detailsPageName}
        />
      );
    case 'Credit Inquiries':
      return (
        <AlertDetailFooter
          factorPath={'creditInquiries'}
          footerType={footerType}
          detailsLinkClick={detailsLinkClick}
          detailsPageName={detailsPageName}
        />
      );
    case cmAlertTypes.FRAUD_ALERT:
      return (
        <FraudAlertFooter
          detailsLinkClick={detailsLinkClick}
          detailsPageName={detailsPageName}
        />
      );
    case 'Score Factors':
      return (
        <ScoreFactorsFooter
          detailsLinkClick={detailsLinkClick}
          detailsPageName={detailsPageName}
        />
      );
    case 'My Actions':
      return (
        <ScoreFactorsFooter
          showActionsLink={true}
          detailsLinkClick={detailsLinkClick}
          detailsPageName={detailsPageName}
        />
      );
    default:
      return null;
  }
};

const AlertDetailsImage = ({ alertType }) => {
  switch (alertType) {
    case cmAlertTypes.IMPROVED_ACCOUNT:
      return (
        <ImprovedAccountLogo role="img" aria-label="ImprovedAccountLogo" />
      );
    case cmAlertTypes.DECREASE_IN_COLLECTION_BALANCES:
      return (
        <DecreaseInCollections role="img" aria-label="DecreaseInCollections" />
      );
    case cmAlertTypes.PAID_COLLECTION:
      return (
        <Image
          imgClass="paidCollection"
          className="margLRAuto margTB24"
          role="img"
          aria-label="paidCollection"
        />
      );
    case cmAlertTypes.PAID_TRADELINE:
      return <PaidTradeline role="img" aria-label="PaidTradeline" />;
    case cmAlertTypes.CREDIT_SCORE_INCREASE:
      return (
        <CreditScoreIncrease role="img" aria-label="CreditScoreIncrease" />
      );
    case cmAlertTypes.CREDIT_SCORE_DECREASE:
      return (
        <CreditScoreDecrease role="img" aria-label="CreditScoreDecrease" />
      );
    case premiumAlertTypes.CHANGE_OF_ADDRESS_ALERT:
    case 'CHANGE_OF_ADDRESS_REPORT':
      return (
        <Image
          imgClass="changeOfAddressAlert"
          className="margLRAuto"
          role="img"
          aria-label="changeOfAddressAlert"
        />
      );
    case premiumAlertTypes.CYBER_AGENT_ALERT:
    case 'CYBER_AGENT_REPORT':
      return (
        <Image
          imgClass="darkWebReportDetails"
          className="margLRAuto margTB24"
          role="img"
          aria-label="darkWebReportDetails"
        />
      );
    case premiumAlertTypes.DARK_WEB_MONITORING:
      return <DarkWebAlert role="img" aria-label="DarkWebAlert" />;
    case premiumAlertTypes.NAME_CHANGE:
      return <NameChangeWebAlert role="img" aria-label="NameChangeWebAlert" />;
    case premiumAlertTypes.ADDRESS_CHANGE:
      return (
        <AddressChangeWebAlert role="img" aria-label="AddressChangeWebAlert" />
      );
    case premiumAlertTypes.CRIMINAL_ACTIVITY:
      return (
        <CourtRecordWebAlert role="img" aria-label="CourtRecordWebAlert" />
      );
    case premiumAlertTypes.NON_CREDIT_ALERT:
    case 'NON_CREDIT_REPORT':
      return (
        <Image
          imgClass="nonCreditLoanData"
          className="margLRAuto"
          role="img"
          aria-label="nonCreditLoanData"
        />
      );
    case premiumAlertTypes.SSN_TRACE_ALERT:
    case 'SSN_TRACE_REPORT':
      return (
        <Image
          imgClass="ssnTraceData"
          className="margLRAuto"
          role="img"
          aria-label="ssnTraceData"
        />
      );
    case premiumAlertTypes.COURT_RECORDS_REPORT:
    case 'COURT_RECORDS_REPORT':
      return (
        <Image
          imgClass="courtRecordsReport"
          className="margLRAuto"
          role="img"
          aria-label="courtRecordsReport"
        />
      );
    default:
      return null;
  }
};

const getPlaceholderValue = (detailsList, placeholder) => {
  let placeholderValue = '';
  detailsList.forEach(item => {
    if (item.title === placeholder) {
      placeholderValue = item.value;
      return;
    }
  });

  return placeholderValue;
};

// Renabling dynamic description support.
const addDynamicDescription = (
  alertType,
  description,
  detailsList,
  alertDetailsConfig,
) => {
  if (description && description.indexOf('[xxx]') >= 0) {
    let placeholder = '';
    switch (alertType) {
      case cmAlertTypes.IMPROVED_ACCOUNT:
        placeholder = getPlaceholderValue(detailsList, 'Status');
        description = placeholder
          ? description.replace('[xxx]', placeholder)
          : 'This account has improved from a derogatory status. Nicely done!';
        break;
      case cmAlertTypes.CARD_OVER_LIMIT:
      case cmAlertTypes.RETAIL_CARD_OVERLIMIT:
        {
          let balance = parseInt(
            decommafy(getPlaceholderValue(detailsList, 'Balance')),
          );
          let limit = parseInt(
            decommafy(getPlaceholderValue(detailsList, 'Credit limit')),
          );
          if (!isNaN(balance) && !isNaN(limit)) {
            placeholder = 'by ' + formatIncome(balance - limit);
          }
        }
        description = description.replace('[xxx]', placeholder);
        break;
      case cmAlertTypes.ATTEMPTED_INQUIRY_LOCK:
        placeholder = getPlaceholderValue(detailsList, 'Requester Name');
        description = description.replace('[xxx]', placeholder);
        break;
      case cmAlertTypes.ATTEMPTED_INQUIRY_FREEZE:
        placeholder = alertDetailsConfig?.requesterName;
        description = description.replace('[xxx]', placeholder);
        break;
      default:
        description = description.replace('[xxx]', '');
        break;
    }
  }

  return description;
};

const PrimaryAlertDetailsView = ({
  datatestid,
  alertDetailsConfig,
  alertDetailsLoading,
  detailsLinkClick,
  isMobileView,
  experimentMeta,
  showNewTemplate,
  iFrameConfig,
  listLinkCallback,
}) => {
  const { showRedesignedAlertDetailVersion } = experimentMeta || {};
  const detailsPageName = `Credit Alert Details - ${alertDetailsConfig.title}`;
  const isMobileByOs = isMobileByOS();
  let showAlertDetailsList = alertDetailsConfig.alertDetailsList.length > 0;
  let showAlertMultiDetailsLists =
    alertDetailsConfig.alertMultiDetailLists.length > 0;
  let showAlertMultiRecordsLists =
    alertDetailsConfig.alertMultiRecordsLists.length > 0;
  let showAlertMultiDetailsRecordsLists =
    alertDetailsConfig.alertMultiDetailsRecordsLists.length > 0;
  let shouldTrackView = false;

  if ((isMobileByOs && isMobileView) || (!isMobileByOs && !isMobileView)) {
    shouldTrackView = true;
  }
  useAlertDetailPageView(alertDetailsLoading, shouldTrackView, {
    'Page Name': detailsPageName,
  });
  let labelText = '';

  if (alertDetailsConfig.reportType === 'tu' && alertDetailsConfig.bureauName) {
    labelText = ` by ${alertDetailsConfig.bureauName}`;
  }

  const isFrameAlert = typeof alertDetailsConfig.alertDetailsList === 'string';

  useScrollToTop();
  return (
    // <RedesignedAlertDetailView
    //   datatestid={datatestid}
    //   alertDetailsConfig={alertDetailsConfig}
    //   labelText={labelText}
    //   showAlertDetailsList={showAlertDetailsList}
    //   showAlertMultiDetailsLists={showAlertMultiDetailsLists}
    //   detailsLinkClick={detailsLinkClick}
    //   detailsPageName={detailsPageName}
    //   isFrameAlert={isFrameAlert}
    //   iFrameConfig={iFrameConfig}
    // />
    <>
      {showRedesignedAlertDetailVersion || showNewTemplate ? (
        <RedesignedAlertDetailView
          datatestid={datatestid}
          alertDetailsConfig={alertDetailsConfig}
          labelText={labelText}
          showAlertDetailsList={showAlertDetailsList}
          showAlertMultiDetailsLists={showAlertMultiDetailsLists}
          showAlertMultiRecordsLists={showAlertMultiRecordsLists}
          showAlertMultiDetailsRecordsLists={showAlertMultiDetailsRecordsLists}
          detailsLinkClick={detailsLinkClick}
          detailsPageName={detailsPageName}
          isFrameAlert={isFrameAlert}
          iFrameConfig={iFrameConfig}
          listLinkCallback={listLinkCallback}
        />
      ) : (
        <AlertDetailView
          datatestid={datatestid}
          alertDetailsConfig={alertDetailsConfig}
          labelText={labelText}
          showAlertDetailsList={showAlertDetailsList}
          showAlertMultiDetailsLists={showAlertMultiDetailsLists}
          showAlertMultiRecordsLists={showAlertMultiRecordsLists}
          showAlertMultiDetailsRecordsLists={showAlertMultiDetailsRecordsLists}
          detailsLinkClick={detailsLinkClick}
          detailsPageName={detailsPageName}
          isFrameAlert={isFrameAlert}
          iFrameConfig={iFrameConfig}
        />
      )}
    </>
  );
};

const GetScoreChangeGuage = ({ datatestid, updatedScore, scoreChange }) => {
  if (updatedScore) {
    return (
      <div className="margB24">
        <ScoreDisplay
          score={updatedScore}
          scoreChange={scoreChange}
          datatestid={datatestid}
        />
        <div className="scoreDialRangeWidth margT6">
          <CreditScoreRange datatestid={`${datatestid}-score-range`} />
        </div>
      </div>
    );
  } else {
    return '';
  }
};

const BureauLogo = ({ bureau }) => {
  if (bureau === 'TransUnion') {
    return (
      <TuLogo
        className="transunionLogo"
        role="img"
        aria-label="transunionLogo"
      />
    );
  } else if (bureau === 'Experian') {
    return <ExperianLogo height={16} role="img" aria-label="ExperianLogo" />;
  } else if (bureau === 'Equifax') {
    return <EquifaxLogo height={10} role="img" aria-label="EquifaxLogo" />;
  } else return '';
};

const RedesignedAlertDetailView = ({
  datatestid,
  alertDetailsConfig,
  labelText,
  showAlertDetailsList,
  showAlertMultiDetailsLists,
  detailsLinkClick,
  detailsPageName,
  isFrameAlert,
  iFrameConfig,
  ...props
}) => {
  const { config } = useConfig();
  let alertTypeValue = '';
  if (isObject(alertDetailsConfig?.alertDetailsList)) {
    alertTypeValue = getPlaceholderValue(
      alertDetailsConfig.alertDetailsList,
      'Type',
    );
  }
  const isInquiryHomeAutoAlert =
    alertTypeValue === inquiryAutoLoanIndustryType ||
    alertTypeValue === inquiryMortgageIndustryType;

  const isScoreChnageAlert =
    alertDetailsConfig.category === cmAlertTypes.CREDIT_SCORE_DECREASE ||
    alertDetailsConfig.category === cmAlertTypes.CREDIT_SCORE_INCREASE;

  const showRefreshTag = showCreditProfileRefreshTag(
    alertDetailsConfig.category,
  );

  let {
    subTitle,
    description,
    scoreChange,
    updatedScore,
    subDescription,
    actionText,
    category,
  } = alertDetailsConfig;
  const isCreditLockAlert =
    category === cmAlertTypes.REPORT_LOCKED ||
    category === cmAlertTypes.REPORT_UNLOCKED ||
    category === cmAlertTypes.REPORT_FROZEN ||
    category === cmAlertTypes.REPORT_UNFROZEN;
  if (isScoreChnageAlert && scoreChange) {
    let absoluteChange = Math.abs(scoreChange);
    description =
      alertDetailsConfig.category === cmAlertTypes.CREDIT_SCORE_INCREASE
        ? `Your score went up by ${absoluteChange} points!`
        : `Your score went down by ${absoluteChange} points.`;
  }
  description = subTitle ?? description;
  description = addDynamicDescription(
    alertDetailsConfig.category,
    description,
    alertDetailsConfig.alertDetailsList,
    alertDetailsConfig,
  );

  const wrapperClass = classnames('padB24 bgWhite margB8', {
    mobilePad8: isFrameAlert,
  });

  const textClass = classnames('padB16', {
    padLR16: isFrameAlert,
  });

  const descClass = classnames('text-16 fontLatoNormal400 margB16', {
    padLR16: isFrameAlert,
  });

  return (
    <>
      <Row className={wrapperClass}>
        <div className={textClass}>
          <Label
            datatestid={`${datatestid}`}
            className="text-28 fontLatoNormal700 margB8"
          >
            {alertDetailsConfig.title}
          </Label>
          <Label
            datatestid={`${datatestid}-reportedOnDate`}
            type="caption"
            className="system_neutral_n300_text"
          >
            {`Reported on ${alertDetailsConfig.date} to `}
            {<BureauLogo bureau={alertDetailsConfig.bureauName} />}
          </Label>
        </div>
        {description && (
          <div data-testid={`${datatestid}-description`} className={descClass}>
            <OText text={description} />
          </div>
        )}
        {isScoreChnageAlert && updatedScore && (
          <GetScoreChangeGuage
            datatestid={`${datatestid}-score-guage`}
            updatedScore={updatedScore}
            scoreChange={scoreChange}
          />
        )}
        {alertDetailsConfig.showImage && (
          <div datatestid={`${datatestid}-image`} className="alertDetailsImage">
            <AlertDetailsImage alertType={alertDetailsConfig.category} />
          </div>
        )}
        {showAlertDetailsList && (
          <AlertDetailsList
            alertDetailsList={alertDetailsConfig.alertDetailsList}
            lineType={alertDetailsConfig.lineType}
            datatestid={`${datatestid}-detailList`}
            outerClass={'padB0'}
            isFrameAlert={isFrameAlert}
            iFrameConfig={iFrameConfig}
          />
        )}
        {showAlertMultiDetailsLists &&
          alertDetailsConfig.alertMultiDetailLists.map((detailItem, index) => (
            <AlertDetailsList
              header={detailItem.header}
              key={`${detailItem.header}-${index}`}
              alertDetailsList={detailItem.alertDetailsList}
              lineType={alertDetailsConfig.lineType}
              datatestid={`${datatestid}-detailList`}
              isFrameAlert={isFrameAlert}
            />
          ))}
        {subDescription && (
          <div className="text-16 m-text-14 padt16">
            {subDescription}
            {isCreditLockAlert && (
              <a href="/creditlock-learnmore" className="link bold margL4">
                Learn more
              </a>
            )}
          </div>
        )}
        {actionText && (
          <Label
            datatestid={`${datatestid}-footer`}
            className="text-16 m-text-14 padt16"
          >
            <AlertDetailsFooter
              footerType={actionText}
              detailsLinkClick={detailsLinkClick}
              detailsPageName={detailsPageName}
            />
          </Label>
        )}
        {showRefreshTag && (
          <div className="text-16 m-text-14 padt16">
            You'll see this change reflected in your account when your credit
            profile is updated in {getNumberOfDaysUntilNextFirstOfMonth()}.
          </div>
        )}
      </Row>

      {isInquiryHomeAutoAlert && (
        <AlertInquiryInfo
          datatestid={`${datatestid}-inquiry-info`}
          {...props}
          isAutoLoanInquiryAlert={
            alertDetailsConfig?.industryType === inquiryAutoLoanIndustryType
          }
        />
      )}

      {alertDetailsConfig.actionTypes && !isInquiryHomeAutoAlert && (
        <>
          <div className="padTB4 bgGrey005"></div>
          <Row
            className="padTB24 bgWhite"
            datatestid={`${datatestid}-action-block`}
          >
            <div>
              <div className="text-20 bold margB16">What you can do</div>
              {alertDetailsConfig.actionTypes.map((type, key) => (
                <AlertActionCardList
                  key={key}
                  type={type}
                  detailsLinkClick={detailsLinkClick}
                  detailsPageName={detailsPageName}
                  datatestid={`${datatestid}-action-block`}
                  config={config}
                  category={alertDetailsConfig?.category}
                />
              ))}
            </div>
          </Row>
        </>
      )}

      {alertDetailsConfig.callHelp && <SomethingWrong />}
    </>
  );
};

const LoansListView = props => {
  const { datatestid, alertDetailsConfig, loansList } = props;
  return loansList?.map((item, index) => {
    return (
      <MultiRecordsAlertDetailsList
        header={item?.header}
        index={index}
        key={`${item?.header}-${index}`}
        alertDetailsList={item}
        lineType={alertDetailsConfig?.lineType}
        datatestid={`${datatestid}-detailList`}
      />
    );
  });
};

const InquiriesListView = props => {
  const { datatestid, alertDetailsConfig, inquiriesList } = props;

  return inquiriesList?.map((item, index) => {
    return (
      <MultiRecordsAlertDetailsList
        header={item?.header}
        index={index}
        key={`${item?.header}-${index}`}
        alertDetailsList={item}
        lineType={alertDetailsConfig?.lineType}
        datatestid={`${datatestid}-detailList`}
      />
    );
  });
};

const AlertRecordsView = props => {
  const {
    datatestid,
    alertDetailsConfig,
    showAlertDetailsList,
    showAlertMultiRecordsLists,
    showAlertMultiDetailsLists,
    isFrameAlert,
    iFrameConfig,
    showAlertMultiDetailsRecordsLists,
  } = props;

  if (showAlertMultiDetailsRecordsLists) {
    const loansList = alertDetailsConfig?.alertMultiDetailsRecordsLists?.[0];
    const inquiriesList =
      alertDetailsConfig?.alertMultiDetailsRecordsLists?.[1];

    return (
      <>
        {inquiriesList && inquiriesList?.length > 0 && (
          <InquiriesListView
            inquiriesList={inquiriesList}
            datatestid={datatestid}
            alertDetailsConfig={alertDetailsConfig}
          />
        )}
        {loansList && loansList?.length > 0 && (
          <LoansListView
            loansList={loansList}
            datatestid={datatestid}
            alertDetailsConfig={alertDetailsConfig}
          />
        )}
      </>
    );
  } else if (showAlertMultiRecordsLists) {
    return alertDetailsConfig?.alertMultiRecordsLists?.map(
      (detailItem, index) => {
        return (
          <MultiRecordsAlertDetailsList
            header={detailItem?.header}
            index={index}
            key={`${detailItem?.header}-${index}`}
            alertDetailsList={detailItem}
            lineType={alertDetailsConfig?.lineType}
            datatestid={`${datatestid}-detailList`}
          />
        );
      },
    );
  } else if (showAlertMultiDetailsLists) {
    return alertDetailsConfig?.alertMultiDetailLists?.map(
      (detailItem, index) => {
        if (
          alertDetailsConfig?.category === 'CYBER_AGENT_ALERT' ||
          alertDetailsConfig?.category === 'CYBER_AGENT_REPORT'
        ) {
          return (
            <MultiRecordsAlertDetailsList
              header={detailItem?.header}
              index={index}
              key={`${detailItem?.header}-${index}`}
              alertDetailsList={detailItem}
              lineType={alertDetailsConfig?.lineType}
              datatestid={`${datatestid}-detailList`}
            />
          );
        } else {
          return (
            <AlertDetailsList
              header={detailItem?.header}
              key={`${detailItem?.header}-${index}`}
              alertDetailsList={detailItem?.alertDetailsList}
              lineType={alertDetailsConfig?.lineType}
              datatestid={`${datatestid}-detailList`}
              isFrameAlert={isFrameAlert}
            />
          );
        }
      },
    );
  } else if (showAlertDetailsList) {
    return (
      <AlertDetailsList
        alertDetailsList={alertDetailsConfig?.alertDetailsList}
        lineType={alertDetailsConfig?.lineType}
        datatestid={`${datatestid}-detailList`}
        isFrameAlert={isFrameAlert}
        iFrameConfig={iFrameConfig}
      />
    );
  } else {
    return null;
  }
};

const AlertDetailView = ({
  datatestid,
  alertDetailsConfig,
  labelText,
  showAlertDetailsList,
  showAlertMultiDetailsLists,
  showAlertMultiRecordsLists,
  showAlertMultiDetailsRecordsLists,
  detailsLinkClick,
  detailsPageName,
  isFrameAlert,
  iFrameConfig,
}) => {
  const wrapperClass = classnames({
    mobilePad8: isFrameAlert,
  });

  const headerPadClass = isFrameAlert ? 'padLR16' : '';

  const textClass = classnames('alertDetailsTitleContainer', {
    padLR16: isFrameAlert,
  });

  const descClass = classnames(
    'alertDetailsDescription system_neutral_n300_text',
    {
      padLR16: isFrameAlert,
    },
  );

  return (
    <>
      <Row className={wrapperClass}>
        <div className={textClass}>
          <TitleContainer
            datatestid={`${datatestid}`}
            className="alertDetailsTitle bold system_neutral_n350_text"
          >
            {alertDetailsConfig?.title}
          </TitleContainer>
          <Label
            datatestid={`${datatestid}-reportedOnDate`}
            type="caption"
            className="system_neutral_n300_text"
          >
            {`Reported on ${alertDetailsConfig?.date}`}
            {labelText}
          </Label>
        </div>
        {alertDetailsConfig?.image && (
          <div datatestid={`${datatestid}-image`} className="alertDetailsImage">
            <AlertDetailsImage alertType={alertDetailsConfig?.category} />
          </div>
        )}

        {alertDetailsConfig?.header && (
          <Label
            datatestid={`${datatestid}-header`}
            type="H5"
            typeHeader={'h5'}
            className={headerPadClass}
          >
            {alertDetailsConfig?.header}
          </Label>
        )}
        {alertDetailsConfig?.description && (
          <Label datatestid={`${datatestid}-description`} className={descClass}>
            {alertDetailsConfig?.description}
          </Label>
        )}

        {alertDetailsConfig?.description2 && (
          <Label
            datatestid={`${datatestid}-description2`}
            className={descClass}
          >
            {alertDetailsConfig?.description2}
          </Label>
        )}

        {alertDetailsConfig?.steps && alertDetailsConfig?.steps?.length > 0 && (
          <Label datatestid={`${datatestid}-steps`} className={descClass}>
            {alertDetailsConfig.steps.map((step, index) => {
              const stepHeading = `Step ${index + 1}`;
              return (
                <>
                  {!alertDetailsConfig?.isStepsTitleInline && (
                    <Label bold className="margB10 text-14">
                      {stepHeading}.
                    </Label>
                  )}
                  <Label className="margB16 text-14">
                    {alertDetailsConfig?.isStepsTitleInline && (
                      <Label className="margR6" inline bold>
                        {stepHeading} :{' '}
                      </Label>
                    )}
                    {step}
                  </Label>
                </>
              );
            })}
          </Label>
        )}

        {alertDetailsConfig?.alertDetailsHeader && (
          <Label
            bold
            datatestid={`${datatestid}-alertDetailsHeader`}
            className="margB16 text-14"
          >
            {alertDetailsConfig?.alertDetailsHeader}
          </Label>
        )}

        <AlertRecordsView
          datatestid={datatestid}
          alertDetailsConfig={alertDetailsConfig}
          showAlertDetailsList={showAlertDetailsList}
          showAlertMultiRecordsLists={showAlertMultiRecordsLists}
          showAlertMultiDetailsLists={showAlertMultiDetailsLists}
          showAlertMultiDetailsRecordsLists={showAlertMultiDetailsRecordsLists}
          isFrameAlert={isFrameAlert}
          iFrameConfig={iFrameConfig}
        />

        {alertDetailsConfig?.footerType && (
          <Label
            type="Body-1"
            datatestid={`${datatestid}-footer`}
            className="alertDetailsFooterText"
          >
            <AlertDetailsFooter
              footerType={alertDetailsConfig?.footerType}
              detailsLinkClick={detailsLinkClick}
              detailsPageName={detailsPageName}
            />
          </Label>
        )}
      </Row>

      {alertDetailsConfig.callHelp && <SomethingWrong />}
      {!alertDetailsConfig.callHelp && <div className="padTB10"></div>}
    </>
  );
};

export const AlertDetailsLoadingView = () => {
  return <PageLoadingMask />;
};

export const AlertDetailsErrorView = () => {
  usePageView(false, {
    'Page Name': 'Credit Alert Details Error',
  });
  return (
    <Row className="cmAlertDetailsError">
      <AlertsErrorMask
        hideButton={true}
        generalMessage="An error happened while retrieving details for this alert."
      />
    </Row>
  );
};

export { PrimaryAlertDetailsView };
